export interface ReferenceEntry {
  id: string;
  title: string;
  syntax: string;
  example?: string;
  result?: string;
  note?: string;
}

export interface ReferenceSection {
  id: string;
  title: string;
  entries: ReferenceEntry[];
}

export const TEMPLATE_REFERENCE: ReferenceSection[] = [
  {
    id: 'ref-sources',
    title: 'Sources / placeholders',
    entries: [
      {
        id: 'ref-sources-body',
        title: 'Request body',
        syntax: '{{body.path.to.field}}',
        example: '{{body.user.email}}',
        result: 'alice@example.com',
        note: 'Nested fields with dot notation; array index with [n], e.g. {{body.items[0].id}}.',
      },
      {
        id: 'ref-sources-rawbody',
        title: 'Raw request body (unparsed)',
        syntax: '{{rawBody}}',
        example: '{{rawBody}}',
        result: '{"id":"evt_1",  "type":"checkout.session.completed"}',
        note: 'The bytes exactly as received, before parsing. Use it with a Crypto step to verify a webhook signature — the signature covers these bytes, and {{body}} has been re-serialised, so key order and whitespace differ. Unavailable for scheduled runs; the placeholder remains unresolved.',
      },
      {
        id: 'ref-sources-headers',
        title: 'Headers (keys lower-cased)',
        syntax: '{{headers.key}}',
        example: '{{headers.x-request-id}}',
        result: '7f3c…',
      },
      {
        id: 'ref-sources-query',
        title: 'Query parameter',
        syntax: '{{query.key}}',
        example: '{{query.page}}',
        result: '2',
      },
      {
        id: 'ref-sources-env',
        title: 'Environment variable',
        syntax: '{{env.KEY}}',
        example: '{{env.SLACK_WEBHOOK_URL}}',
        result: 'https://hooks.slack.com/…',
      },
      {
        id: 'ref-sources-node',
        title: 'Output of a previous step (node ID)',
        syntax: '{{<NodeId>.path}}',
        example: '{{http-1.body.id}}',
        result: '42',
        note: 'The stable identifier is the node ID, not the UI alias.',
      },
      {
        id: 'ref-sources-dynamic-key',
        title: 'Dynamic key (composed at runtime)',
        syntax: '{{env.PREFIX_$(source.path)}}',
        example: '{{env.TBX_URL_$(body.customerId)}}',
        result: 'https://tbx.example.com/4711',
        note:
          'Works for every source, not just env. Resolved once — the inserted value is never ' +
          'read as a template again. If the inner reference is empty, or its value contains ' +
          '. [ ] | { } $ ( ) or whitespace, the whole placeholder stays unresolved.',
      },
      {
        id: 'ref-sources-item',
        title: 'Current element (inside ForEach)',
        syntax: '{{item}} / {{item.field}}',
        example: '{{item.email}}',
        result: 'bob@example.com',
      },
      {
        id: 'ref-sources-batch',
        title: 'Batch event by index',
        syntax: '{{batch[i].field}}',
        example: '{{batch[0].orderId}}',
        result: '1001',
      },
      {
        id: 'ref-sources-now',
        title: 'Current timestamp (UTC, ISO-8601)',
        syntax: '{{now}}',
        result: '2026-06-17T09:30:00.0000000Z',
      },
      {
        id: 'ref-sources-length',
        title: 'Array length (virtual property)',
        syntax: '{{source.path.length}}',
        example: '{{http-1.body.items.length}}',
        result: '3',
        note: 'Also {{batch.length}} for the number of batch events.',
      },
    ],
  },
  {
    id: 'ref-fallback',
    title: 'Fallbacks (??)',
    entries: [
      {
        id: 'ref-fallback-chain',
        title: 'Fallback chain',
        syntax: '{{a}} ?? {{b}} ?? literal',
        example: '{{body.name}} ?? {{body.login}} ?? unknown',
        result: 'unknown',
        note: 'Left to right; the first non-null/non-empty value wins. A bare literal on the right is returned as-is. If every placeholder resolves to null/empty, the last placeholder is returned unresolved.',
      },
    ],
  },
  {
    id: 'ref-math',
    title: 'Math (math:)',
    entries: [
      {
        id: 'ref-math-basic',
        title: 'Arithmetic with $ references',
        syntax: '{{math: ($source.path ?? N) <op> …}}',
        example: '{{math: ($body.duration ?? 0) / 3600}}',
        result: '2',
        note: 'References use the $ prefix (not {{}}). ?? N provides a numeric fallback. Whole results are returned as integers.',
      },
    ],
  },
  {
    id: 'ref-pipes',
    title: 'Pipes',
    entries: [
      {
        id: 'ref-pipes-first',
        title: 'first / last — first/last array element',
        syntax: '{{source.array | first}}',
        example: '{{http-1.body.items | first}}',
        result: '{ "id": 1, … }',
      },
      {
        id: 'ref-pipes-count',
        title: 'count — number of array elements',
        syntax: '{{source.array | count}}',
        example: '{{http-1.body.items | count}}',
        result: '3',
      },
      {
        id: 'ref-pipes-path',
        title: 'path: — property of an object',
        syntax: '{{source.obj | path: field}}',
        example: '{{http-1.body | path: id}}',
        result: '42',
      },
      {
        id: 'ref-pipes-map',
        title: 'map: — collect field values from an array',
        syntax: '{{source.array | map: field}}',
        example: '{{http-1.body.items | map: id}}',
        result: '[1,2,3]',
      },
      {
        id: 'ref-pipes-find',
        title: 'find: — first element matching a condition',
        syntax: '{{source.array | find: field == value}}',
        example: '{{http-1.body.items | find: status == "done"}}',
        result: '{ "status": "done", … }',
        note: 'Case-insensitive. RHS quoted ("x"), bare (x) or dynamic ($body.field).',
      },
      {
        id: 'ref-pipes-orderby',
        title: 'orderBy: — sort an array',
        syntax: '{{source.array | orderBy: field [desc]}}',
        example: '{{http-1.body.items | orderBy: createdAt desc}}',
        result: '[ … ]',
        note: 'Numeric fields sort numerically, otherwise lexicographically. desc reverses the order.',
      },
      {
        id: 'ref-pipes-date',
        title: 'date: — format an ISO date (.NET format)',
        syntax: '{{source.field | date: yyyy-MM-dd}}',
        example: '{{body.createdAt | date: yyyy-MM-dd}}',
        result: '2026-06-17',
      },
      {
        id: 'ref-pipes-dateadd',
        title: 'dateAdd: — offset an ISO date',
        syntax: '{{source.field | dateAdd: 1d}}',
        example: '{{body.createdAt | dateAdd: 1d}}',
        result: '2026-06-18T…Z',
        note: 'Units: d (days), h (hours), m (minutes). Negative offsets go back in time, e.g. {{now | dateAdd: -7d}}. The result is an ISO string.',
      },
      {
        id: 'ref-pipes-toutcfrom',
        title: 'toUtcFrom: — local time in a timezone to UTC',
        syntax: '{{source.field | toUtcFrom: Europe/Berlin}}',
        example: '{{body.start | toUtcFrom: Europe/Berlin}}',
        result: '2026-07-01T07:00:00Z (from 2026-07-01T09:00:00)',
        note: 'Reads a date without offset as local time in the given IANA timezone and returns UTC; daylight saving is applied per date (09:00 Berlin is 07:00Z in July, 08:00Z in January). The timezone may be dynamic: toUtcFrom: $body.record.timezone_id. A value that already carries Z or an offset (+02:00) is an instant and is returned as that instant in UTC, whatever the zone. An unknown timezone leaves the placeholder unresolved. Chain date: to reformat. Former name: tz:, still accepted.',
      },
      {
        id: 'ref-pipes-fromutcto',
        title: 'fromUtcTo: — UTC to local time in a zone',
        syntax: '{{source.field | fromUtcTo: Europe/Berlin}}',
        example: '{{http-1.body.endOn | fromUtcTo: Europe/Berlin | date: yyyy-MM-dd}}',
        result: '2026-10-26',
        note: 'A value without Z or offset counts as UTC. The result has no offset, so date: formats the local wall-clock time. The zone may be a $ref.',
      },
      {
        id: 'ref-pipes-epoch',
        title: 'epoch: — ISO date to Unix timestamp',
        syntax: '{{source.field | epoch: ms}}',
        example: '{{body.closedate | epoch: ms}}',
        result: '1789689600000',
        note: 'No unit or "s" gives seconds, "ms" milliseconds. Meta expects seconds, LinkedIn milliseconds.',
      },
      {
        id: 'ref-pipes-sum',
        title: 'sum: — add up an array',
        syntax: '{{source.field | sum: amount}}',
        example: '{{body.items | sum: amount}}',
        result: '3500',
        note: 'Without an argument it sums numbers directly. Non-numeric entries count as zero.',
      },
      {
        id: 'ref-pipes-split',
        title: 'split: — delimited string to array',
        syntax: '{{source.field | split: ;}}',
        example: '{{body.hs_attachment_ids | split: ;}}',
        result: '["123","456"]',
        note: 'Entries are trimmed and empty ones dropped. Quote the separator to include spaces: split: ", ". An array passes through unchanged; nothing left to split leaves the placeholder unresolved. Use it as a ForEach arrayExpression.',
      },
      {
        id: 'ref-pipes-truncate',
        title: 'truncate: — cut a string to a maximum length',
        syntax: '{{source.field | truncate: 250}}',
        example: '{{body.issue.fields.summary | truncate: 250}}',
        result: 'first 250 characters',
        note: 'For target fields with a length limit. Shorter strings and non-strings pass through unchanged.',
      },
      {
        id: 'ref-pipes-string',
        title: 'string — keep free text a string',
        syntax: '{{source.field | string}}',
        example: '"note": "{{body.comment | string}}"',
        result: '"note": "12345" (not 12345)',
        note: 'Must be the last pipe. Stops the JSON coercion in HTTP bodies and Transform from turning "12345", "true" or "null" into a number, boolean or null. A real JSON null stays null. Add it again in every step that writes the value into JSON.',
      },
      {
        id: 'ref-pipes-chain',
        title: 'Chaining pipes',
        syntax: '… | pipe1 | pipe2 | …',
        example: '{{body.items | find: id == 2 | path: name}}',
        result: 'Widget',
        note: 'Pipes can be combined in any order.',
      },
    ],
  },
  {
    id: 'ref-coercion',
    title: 'JSON type coercion',
    entries: [
      {
        id: 'ref-coercion-bool',
        title: 'Quotes are stripped for JSON literals',
        syntax: '"field": "{{placeholder}}"',
        example: '"completed": "{{body.isDone}}"',
        result: '"completed": true',
        note: 'When a placeholder is the ENTIRE value of a JSON string field (HTTP body / Transform output) and resolves to a JSON literal (null, true, false, integer), the surrounding quotes are stripped. Decimals and arbitrary strings stay quoted and are JSON-escaped, including line breaks. Placeholders embedded in a larger string ("Hello {{name}}") are not escaped — keep free text as the whole value.',
      },
      {
        id: 'ref-coercion-string',
        title: 'Keep free text a string with | string',
        syntax: '"field": "{{placeholder | string}}"',
        example: '"note": "{{body.comment | string}}"',
        result: '"note": "true" (not true)',
        note: 'For free text that may look like a literal, e.g. a comment "12345", "true" or "null". | string must be the last pipe, and a later step that reads the value needs it again.',
      },
    ],
  },
  {
    id: 'ref-recipes',
    title: 'Practical recipes',
    entries: [
      {
        id: 'ref-recipes-find',
        title: 'Find an entry in a list and get a field',
        syntax: '{{http-1.body.items | find: status == "done" | path: id}}',
        result: '2',
      },
      {
        id: 'ref-recipes-forward',
        title: 'Forward a webhook body field',
        syntax: '"email": "{{body.user.email}}"',
      },
      {
        id: 'ref-recipes-token',
        title: 'Use a token lookup in an HTTP header',
        syntax: 'Authorization: Bearer {{token-1.accessToken}}',
      },
      {
        id: 'ref-recipes-date',
        title: 'Add 1 day to a date, then format it',
        syntax: '{{body.createdAt | dateAdd: 1d | date: yyyy-MM-dd}}',
      },
      {
        id: 'ref-recipes-tz',
        title: 'Convert a local appointment time to UTC',
        syntax: '{{body.record.start | toUtcFrom: $body.record.timezone_id | date: yyyy-MM-dd HH:mm}}',
        result: '2026-07-01 07:00',
        note: 'For systems that send wall-clock time plus a separate timezone field. Use a literal zone (toUtcFrom: Europe/Berlin) when the source has none.',
      },
      {
        id: 'ref-recipes-since',
        title: 'Query everything changed in the last 7 days',
        syntax: '?updatedSince={{now | dateAdd: -7d | date: yyyy-MM-dd}}',
        result: '?updatedSince=2026-06-10',
      },
      {
        id: 'ref-recipes-signature',
        title: 'Verify an inbound webhook signature (HubSpot v3)',
        syntax: 'Crypto: hmac-sha256 over POST{{env.FLOWFORGE_WEBHOOK_URL}}{{rawBody}}{{headers.x-hubspot-request-timestamp}}, encoding base64',
        result: '{{crypto-1.value}} → compare with {{headers.x-hubspot-signature-v3}}',
        note: 'The signed string is rarely the body alone — HubSpot signs method + URI + body + timestamp, and every part is addressable. Also compare the timestamp against {{now | epoch: ms}} to reject replays. Stripe cannot be verified yet: it signs <timestamp>.<body> and the timestamp only exists inside the composite t=…,v1=… header; split: , separates the parts, but nothing strips the t= prefix.',
      },
      {
        id: 'ref-recipes-hash-email',
        title: 'Hash an email for LinkedIn / Google / Meta',
        syntax: 'Crypto: sha256, normalize lower+trim, encoding hex',
        result: '{{crypto-1.value}} → 973dfe46…813b',
        note: 'Normalising first is not optional: a different spelling hashes to something else and silently never matches. Never hash Meta fbc/fbp — those go unhashed.',
      },
      {
        id: 'ref-recipes-fallback',
        title: 'Fall back to a default value',
        syntax: '{{body.name}} ?? {{body.login}} ?? unknown',
      },
    ],
  },
];

/** Maps a node config field to the most relevant reference section anchor. */
export const FIELD_SECTION_HINT = 'ref-sources';
